import { Injectable } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { IManualDeliverableStatus } from '@shared/interfaces/campaign-analytics-api.interface';
import { EMPTY, Observable } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { ManualDeliverableStatusService } from './manual-deliverable-status.service';

interface IManualDeliverableStatusState {
  error: string | null;
  loading: boolean;
  manualDeliverableStatus: IManualDeliverableStatus | null;
}

const initialState: IManualDeliverableStatusState = {
  error: null,
  loading: false,
  manualDeliverableStatus: null,
};

@Injectable()
export class ManualDeliverableStatusStore extends ComponentStore<IManualDeliverableStatusState> {
  readonly manualDeliverableStatus$ = this.selectSignal((s) => s.manualDeliverableStatus);
  readonly loading$ = this.selectSignal((s) => s.loading);
  readonly error$ = this.selectSignal((s) => s.error);

  public readonly createManualDeliverableStatus = this.effect((trigger$: Observable<IManualDeliverableStatus>) => {
    return trigger$.pipe(
      tap(() => this.patchState({ error: null, loading: true })),
      switchMap((payload) =>
        this.service.createManualDeliverableStatus(payload).pipe(
          tap((manualDeliverableStatus: IManualDeliverableStatus) => {
            this.patchState({
              error: null,
              loading: false,
              manualDeliverableStatus,
            });
          }),
          catchError(() => {
            this.patchState({ error: 'Failed to create manual deliverable status', loading: false });

            return EMPTY;
          }),
        ),
      ),
    );
  });

  public readonly updateManualDeliverableStatus = this.effect((params$: Observable<IManualDeliverableStatus>) =>
    params$.pipe(
      tap(() => {
        this.patchState({ error: null, loading: true });
      }),
      switchMap((params) =>
        this.service.updateManualDeliverableStatus(params.id!, params).pipe(
          tap((manualDeliverableStatus: IManualDeliverableStatus) => {
            this.patchState({
              error: null,
              loading: false,
              manualDeliverableStatus,
            });
          }),
          catchError(() => {
            this.patchState({ error: 'Failed to update manual status', loading: false });

            return EMPTY;
          }),
        ),
      ),
    ),
  );

  constructor(private readonly service: ManualDeliverableStatusService) {
    super(initialState);
  }
}
